/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QException} from "@temple-of-syrinx/qqq-frontend-core/lib/exceptions/QException";
import {Capability} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/Capability";
import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QFieldType} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldType";
import {QInstance} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QInstance";
import {QProcessMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QProcessMetaData";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QTableSection} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableSection";
import {QTableVariant} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableVariant";
import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import {QueryJoin} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QueryJoin";
import DynamicFormUtils, {DynamicFormFieldDefinition} from "qqq/components/forms/DynamicFormUtils";
import {evaluateFieldRules, runOnLoadFormAdjuster, runOnChangeFormAdjuster, FormAdjusterCallbacks} from "qqq/components/forms/formAdjustersAndRules";
import {ChildRecordListData} from "qqq/components/widgets/misc/RecordGridWidget";
import {FieldRule} from "qqq/models/fields/FieldRules";
import {RecordScreenMode} from "qqq/pages/records/RecordScreenContext";
import HistoryUtils from "qqq/utils/HistoryUtils";
import Client from "qqq/utils/qqq/Client";
import ProcessUtils from "qqq/utils/qqq/ProcessUtils";
import TableUtils from "qqq/utils/qqq/TableUtils";
import ValueUtils from "qqq/utils/qqq/ValueUtils";
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import * as Yup from "yup";
import QContext from "QContext";

const qController = Client.getInstance();

const TABLE_VARIANT_LOCAL_STORAGE_KEY_ROOT = "qqq.tableVariant";


/***************************************************************************
 ** Read a childRecordList widget's child-table meta-data, preferring the newer
 ** childFrontendTableMetaData field over the legacy childTableMetaData one -
 ** matching what RecordGridWidget.getTableMetaDataFromWidgetData does.
 **
 ** The backend is migrating from childTableMetaData (which was not a
 ** QFrontendTableMetaData) to childFrontendTableMetaData (which is, so it
 ** carries join tables as tables rather than as names).  Every caller that
 ** reads the child table off widget data has to share the preference, or it
 ** goes blind the day a widget emits only the new field.
 **
 ** No widget emits only the new field today (the ChildRecordListData
 ** constructor always fills in both), so this is preference for the
 ** migration's sake rather than a fix for anything currently visible.
 **
 ** Returns whatever the backend sent - a plain object in practice.  It does
 ** NOT wrap in a QTableMetaData, since the callers here read only name/label,
 ** and wrapping would change what they hand downstream.
 ***************************************************************************/
export function resolveChildTableMetaData(widgetData: ChildRecordListData | any): any
{
   return (widgetData?.childFrontendTableMetaData ?? widgetData?.childTableMetaData);
}


/***************************************************************************
 **
 ***************************************************************************/
function objectToMap(object: { [key: string]: any }): Map<string, any>
{
   if (object == null)
   {
      return (null);
   }

   const rs = new Map<string, any>();
   for (let key in object)
   {
      rs.set(key, object[key]);
   }
   return rs;
}


/***************************************************************************
 ** whether a widget's validation group holds nothing at all - the one
 ** registration that can be skipped without inspecting any schema.
 ***************************************************************************/
function checkValidationsAreEmpty(validations: { [key: string]: Yup.Schema }): boolean
{
   return (Object.keys(validations ?? {}).length === 0);
}


/***************************************************************************
 ** Options for modal / embedded usage of useRecordScreen.
 ***************************************************************************/
export interface UseRecordScreenOptions
{
   defaultValues?: { [key: string]: any };
   disabledFields?: { [key: string]: boolean } | string[];
   overrideTableMetaData?: QTableMetaData;
   skipNavigation?: boolean;
   onSubmitCallback?: (values: any, tableName: string) => void;
   scrollCorrectionRef?: React.MutableRefObject<{fieldName: string; yBefore: number} | null>;
}


/***************************************************************************
 **
 ***************************************************************************/
export interface UseRecordScreenResult
{
   mode: RecordScreenMode;
   setMode: (mode: RecordScreenMode) => void;

   record: QRecord;
   tableMetaData: QTableMetaData;
   metaData: QInstance;

   // sections
   tableSections: QTableSection[];
   t1Section: QTableSection;
   nonT1Sections: QTableSection[];
   sectionVisibility: { [key: string]: boolean };

   // form state
   formFields: { [key: string]: DynamicFormFieldDefinition };
   formFieldsBySection: Map<string, DynamicFormFieldDefinition[]>;
   initialValues: { [key: string]: any };
   formValidations: { [key: string]: Yup.Schema };

   // field rules
   fieldRules: FieldRule[];

   // status
   loading: boolean;
   notFoundMessage: string;
   errorMessage: string;
   successMessage: string;
   warningMessage: string;
   notAllowedError: string;

   // actions
   saveRecord: (values: any) => Promise<QRecord>;
   deleteRecord: () => Promise<void>;
   handleFieldChange: (fieldName: string, oldValue: any, newValue: any, valueChangesToMake: { [fieldName: string]: any }) => void;
   handleFieldBlur: (fieldName: string, value: any) => void;
   reloadWidget: (widgetName: string, additionalParams?: { [key: string]: any }) => void;
   reloadRecordData: () => void;
   widgetReloadCounters: Record<string, number>;
   widgetReloadAdditionalParams: Record<string, { [key: string]: any }>;
   widgetRefetchSignals: Record<string, number>;

   // processes & actions
   allTableProcesses: QProcessMetaData[];

   // table variant
   tableVariant: QTableVariant;

   // collapsible sections
   collapsibleSectionOpenStates: Record<string, boolean>;
   toggleCollapsibleSectionOpenState: (sectionName: string) => void;
   openCollapsedSectionsWithErrorFields: (errorFieldNames: string[]) => void;

   // message setters
   setSuccessMessage: (msg: string) => void;
   setWarningMessage: (msg: string) => void;
   setErrorMessage: (msg: string) => void;
   clearPageLevelAlerts: () => void;

   // refs for form adjuster access
   setFieldValueRef: React.MutableRefObject<(name: string, value: any, shouldValidate?: boolean) => void>;
   formValuesRef: React.MutableRefObject<{ [key: string]: any }>;

   // widget sub-validations
   addSubValidations: (name: string, validations: { [key: string]: Yup.Schema }) => void;

   // disabled fields (for modal usage)
   disabledFields: { [key: string]: boolean };

   // child record management (edit mode)
   childListWidgetData: { [name: string]: ChildRecordListData };
   showEditChildForm: any;
   setShowEditChildForm: (val: any) => void;
   openAddChildRecord: (widgetName: string, widgetData: ChildRecordListData) => void;
   openEditChildRecord: (widgetName: string, widgetData: ChildRecordListData, rowIndex: number) => void;
   deleteChildRecord: (widgetName: string, rowIndex: number) => void;
   submitEditChildForm: (values: any, tableName: string) => void;
   bulkAppendChildRecords: (widgetName: string, rowsValues: { [fieldName: string]: any }[]) => Promise<void>;
}


/***************************************************************************
 ** Custom hook that manages all data loading and state for the RecordScreen.
 ***************************************************************************/
export function useRecordScreen(tableName: string, recordId?: string, initialMode?: RecordScreenMode, isCopy?: boolean, options?: UseRecordScreenOptions): UseRecordScreenResult
{
   const navigate = useNavigate();
   const location = useLocation();
   const {setPageHeader, setTableMetaData: setQContextTableMetaData, setTableProcesses, recordAnalytics, helpHelpActive} = useContext(QContext);

   const skipNavigation = options?.skipNavigation ?? false;

   const [mode, setMode] = useState<RecordScreenMode>(initialMode ?? (recordId ? "view" : "create"));
   const [loading, setLoading] = useState(true);

   // sync mode with initialMode prop when it changes (e.g., navigating between view/edit routes)
   useEffect(() =>
   {
      const newMode = initialMode ?? (recordId ? "view" : "create");
      switchMode(newMode);
   }, [initialMode, recordId]);

   // clear alerts when entering edit mode (covers Back-button navigation to /edit)
   useEffect(() =>
   {
      if (mode === "edit")
      {
         clearPageLevelAlerts();
      }
   }, [mode]);
   const [record, setRecord] = useState<QRecord>(null);
   const [tableMetaData, setTableMetaData] = useState<QTableMetaData>(null);
   const [metaData, setMetaData] = useState<QInstance>(null);

   const [tableSections, setTableSections] = useState<QTableSection[]>([]);
   const tableSectionsRef = useRef<QTableSection[]>([]);
   tableSectionsRef.current = tableSections;
   const [t1Section, setT1Section] = useState<QTableSection>(null);
   const [nonT1Sections, setNonT1Sections] = useState<QTableSection[]>([]);
   const [sectionVisibility, setSectionVisibility] = useState<{ [key: string]: boolean }>({});

   const [formFields, setFormFields] = useState<{ [key: string]: DynamicFormFieldDefinition }>({});
   const formFieldsRef = useRef<{ [key: string]: DynamicFormFieldDefinition }>({});
   formFieldsRef.current = formFields;
   const [formFieldsBySection, setFormFieldsBySection] = useState<Map<string, DynamicFormFieldDefinition[]>>(new Map());
   const [initialValues, setInitialValues] = useState<{ [key: string]: any }>({});
   const [formValidations, setFormValidations] = useState<{ [key: string]: Yup.Schema }>({});

   ////////////////////////////////////////////////////////////////////////////////////////
   // allows widgets (e.g., CronUIWidget, GridBuilderWidget) to register their own       //
   // field validations with Formik.                                                     //
   //                                                                                    //
   // Kept per-widget-name, and REPLACED on each call, rather than merged into one flat  //
   // map.  A widget whose set of fields changes - a grid or row builder, where removing //
   // a row removes its inputs - would otherwise leave the removed fields' validations   //
   // in the schema forever.  Those fields have no value and no rendered input, so       //
   // Formik fails validation with nothing on screen to explain it, and pressing Save    //
   // silently does nothing.                                                             //
   ////////////////////////////////////////////////////////////////////////////////////////
   const [subValidationsByWidgetName, setSubValidationsByWidgetName] = useState<{ [widgetName: string]: { [key: string]: Yup.Schema } }>({});

   ////////////////////////////////////////////////////////////////////////////////////////
   // field names whose validation an on-change form adjuster has replaced.  These win   //
   // over any widget group - see the comment on effectiveFormValidations below.  A ref, //
   // not state, because handleFieldMetaDataReplaced's own setFormValidations already    //
   // schedules the render that reads this.                                              //
   ////////////////////////////////////////////////////////////////////////////////////////
   const adjusterReplacedFieldNames = useRef<Set<string>>(new Set());

   const addSubValidations = useCallback((name: string, validations: { [key: string]: Yup.Schema }) =>
   {
      setSubValidationsByWidgetName(prev =>
      {
         ////////////////////////////////////////////////////////////////////////////////////
         // the group is REPLACED, and the only registration skipped is empty-over-empty.  //
         //                                                                                //
         // Comparing the incoming schemas against the stored ones cannot be made sound    //
         // here.  Identity never holds, because every caller builds its schemas fresh on  //
         // each call.  Yup's describe() reports the tests a schema declares but not the   //
         // functions behind them, so two Yup.mixed().test(name, message, fn) schemas      //
         // whose fn closes over DIFFERENT state describe identically - which is exactly   //
         // the shape GridBuilderWidget registers, with the whole rule inside the closure. //
         // Skipping on that comparison keeps the stale closure, and a rule that should    //
         // now block Save silently stops doing so.                                        //
         //                                                                                //
         // Replacing unconditionally costs one render of this screen per registration.    //
         // Every caller registers from an effect with bounded deps - a structural edit in //
         // a grid or row builder, or a table's meta-data arriving - so that is a user     //
         // action's worth of renders, not a per-keystroke one, and since no caller's deps //
         // include anything this write feeds, it cannot loop.                             //
         ////////////////////////////////////////////////////////////////////////////////////
         if (prev[name] === validations || (checkValidationsAreEmpty(prev[name]) && checkValidationsAreEmpty(validations)))
         {
            return (prev);
         }

         return ({...prev, [name]: validations});
      });
   }, []);

   ///////////////////////////////////////////////////////////////////////////////////////
   // the table's own field validations, plus each widget's current group.  Widget      //
   // groups are layered on top so a widget can drop a field simply by no longer        //
   // registering it.                                                                   //
   //                                                                                   //
   // Except for fields an on-change adjuster has since replaced: those go back on top. //
   // A widget that registers REAL table field names (CronUIWidget does, and registers  //
   // once) would otherwise permanently shadow handleFieldMetaDataReplaced - which      //
   // exists precisely so an adjuster that un-requires and hides a field can clear its  //
   // Yup rule.  Left shadowed, the stale required rule blocks Save with nothing on     //
   // screen to explain it.                                                             //
   ///////////////////////////////////////////////////////////////////////////////////////
   const effectiveFormValidations = useMemo(() =>
   {
      const result = {...formValidations};
      for (const widgetName of Object.keys(subValidationsByWidgetName))
      {
         Object.assign(result, subValidationsByWidgetName[widgetName]);
      }

      adjusterReplacedFieldNames.current.forEach(fieldName =>
      {
         result[fieldName] = formValidations[fieldName];
      });

      return (result);
   }, [formValidations, subValidationsByWidgetName]);

   const [fieldRules, setFieldRules] = useState<FieldRule[]>([]);

   const [notFoundMessage, setNotFoundMessage] = useState<string>(null);
   const [errorMessage, setErrorMessage] = useState<string>(null);
   const [successMessage, setSuccessMessage] = useState<string>(null);
   const [warningMessage, setWarningMessage] = useState<string>(null);
   const [notAllowedError, setNotAllowedError] = useState<string>(null);

   const [allTableProcesses, setAllTableProcesses] = useState<QProcessMetaData[]>([]);

   const [collapsibleSectionOpenStates, setCollapsibleSectionOpenStates] = useState<Record<string, boolean>>({});
   const [widgetReloadCounters, setWidgetReloadCounters] = useState<Record<string, number>>({});
   const [widgetReloadAdditionalParams, setWidgetReloadAdditionalParams] = useState<Record<string, { [key: string]: any }>>({});

   //////////////////////////////////////////////////////////////////////////////////////
   // per-widget signal that triggers an IN-PLACE refetch (no remount/blank), distinct //
   // from widgetReloadCounters (which feed the React key and force a remount)         //
   //////////////////////////////////////////////////////////////////////////////////////
   const [widgetRefetchSignals, setWidgetRefetchSignals] = useState<Record<string, number>>({});

   ////////////////////////////////////////////////////////////////////////////////////
   // set when a save has already refreshed every widget on the screen, so the       //
   // post-save view-mode on-load adjuster doesn't ask for the same widgets a second //
   // time.  the adjuster's request follows an awaited network round-trip, so it     //
   // lands after the save's refresh has already gone out.  switchMode reads and     //
   // clears this synchronously - see there for why it must not be cleared in a      //
   // promise callback.                                                              //
   ////////////////////////////////////////////////////////////////////////////////////
   const saveScheduledWidgetRefetchRef = useRef<boolean>(false);

   // refs for form adjuster access to Formik helpers
   const setFieldValueRef = useRef<(name: string, value: any, shouldValidate?: boolean) => void>(() =>
   {
   });
   const formValuesRef = useRef<{ [key: string]: any }>({});

   // track current mode in a ref so async callbacks read the live value, not a stale closure
   const modeRef = useRef<RecordScreenMode>(mode);
   useEffect(() =>
   {
      modeRef.current = mode;
   }, [mode]);

   // normalize disabledFields option into a {fieldName: true} map
   const [disabledFields] = useState<{ [key: string]: boolean }>(() =>
   {
      if (!options?.disabledFields) return {};
      if (Array.isArray(options.disabledFields))
      {
         const map: { [key: string]: boolean } = {};
         for (const f of options.disabledFields)
         {
            map[f] = true;
         }
         return map;
      }
      ////////////////////////////////////////////////////////////////////////////
      // values may be FK values (e.g., {"orderId": 4}) — normalize all to true //
      ////////////////////////////////////////////////////////////////////////////
      const map: { [key: string]: boolean } = {};
      for (const key in options.disabledFields)
      {
         map[key] = true;
      }
      return map;
   });

   // counter to force a full data reload (e.g., #reload hash)
   const [dataLoadCounter, setDataLoadCounter] = useState(0);

   // child record management (edit mode)
   const [childListWidgetData, setChildListWidgetData] = useState<{ [name: string]: ChildRecordListData }>({});
   const [showEditChildForm, setShowEditChildForm] = useState<any>(null);

   let tableVariant: QTableVariant = null;
   const tableVariantLocalStorageKey = `${TABLE_VARIANT_LOCAL_STORAGE_KEY_ROOT}.${tableName}`;
   if (localStorage.getItem(tableVariantLocalStorageKey))
   {
      tableVariant = JSON.parse(localStorage.getItem(tableVariantLocalStorageKey));
   }

   /////////////////////////////////////////////////////////////////////////////////////////////////////////
   // callbacks for the imported runOnLoadFormAdjuster — delegates side effects back to this hook's state //
   /////////////////////////////////////////////////////////////////////////////////////////////////////////
   const formAdjusterCallbacks: FormAdjusterCallbacks = {
      applyUpdatedSections: (response) => applyUpdatedSectionsFromFormAdjuster(tableMetaData, response),
      onFormDisabled: (message) => setNotAllowedError(message),
      shouldApplyFormDisabled: () =>
      {
         const modeToCheck = modeRef.current;
         return modeToCheck === "edit" || modeToCheck === "create";
      },
      disableAllFields: () => disableAllFields(tableMetaData),
   };

   const onChangeFormAdjusterCallbacks: FormAdjusterCallbacks = {
      ...formAdjusterCallbacks,
      applyUpdatedSections: (response) =>
      {
         applyUpdatedSectionsFromFormAdjuster(tableMetaData, response);
         figureOutSectionVisibility(tableMetaData);
      },
   };


   /***************************************************************************
    ** when a record can't be loaded (not-found, not-allowed, or a server error),
    ** drop the record and page-header left over from a previously-viewed record,
    ** so its label doesn't leak into the navbar header above the error alert.
    ** clearing the record also stops the mode/record sync effect from
    ** re-asserting the stale header when navigating to a bad id in the same table.
    ***************************************************************************/
   /***************************************************************************
    ** clear the three page-level alert banners (success / warning / error) as a
    ** group - used wherever a new outcome should not stack on top of a stale one
    ** (entering edit mode, canceling out of edit, before a save).
    ***************************************************************************/
   function clearPageLevelAlerts(): void
   {
      setSuccessMessage(null);
      setWarningMessage(null);
      setErrorMessage(null);
   }


   function clearStaleRecordAndPageHeader(): void
   {
      setRecord(null);

      /////////////////////////////////////////////////////////////////////////////////
      // only clear the (global) page header when this hook owns it - modal/embedded //
      // usage (skipNavigation) doesn't set it, so it must not wipe the parent's.    //
      /////////////////////////////////////////////////////////////////////////////////
      if (!skipNavigation)
      {
         setPageHeader(null);
      }
   }


   /***************************************************************************
    ** load metadata, record, processes, form fields, etc.
    ***************************************************************************/
   useEffect(() =>
   {
      (async () =>
      {
         setLoading(true);

         ///////////////////////////////////////////////////////////////////////////
         // clear "outcome" state from a prior record load, so it doesn't survive //
         // into this load - e.g. a not-found/not-allowed/error message from the  //
         // record we navigated away from must not stick onto a now-valid record. //
         ///////////////////////////////////////////////////////////////////////////
         setNotFoundMessage(null);
         setNotAllowedError(null);
         setErrorMessage(null);

         try
         {
            ///////////////////////////////////////////////////////////////////////
            // fetch table meta data - clone it in case form adjusters modify it //
            ///////////////////////////////////////////////////////////////////////
            let tableMetaData: QTableMetaData;
            if (options?.overrideTableMetaData)
            {
               const override = options.overrideTableMetaData;
               tableMetaData = (override instanceof QTableMetaData ? override : new QTableMetaData(override)).clone();
            }
            else
            {
               tableMetaData = (await qController.loadTableMetaData(tableName)).clone();
            }
            setTableMetaData(tableMetaData);
            if (!skipNavigation)
            {
               setQContextTableMetaData(tableMetaData);
            }

            const metaData = await qController.loadMetaData();
            setMetaData(metaData);
            ValueUtils.qInstance = metaData;

            ///////////////////////
            // setup field rules //
            ///////////////////////
            const mdbMetaData = tableMetaData?.supplementalTableMetaData?.get("materialDashboard");
            if (mdbMetaData?.fieldRules)
            {
               setFieldRules(mdbMetaData.fieldRules.map((r: any) => r as FieldRule));
            }

            ////////////////////
            // load processes //
            ////////////////////
            if (!skipNavigation)
            {
               const processesForTable = ProcessUtils.getProcessesForTable(metaData, tableName);
               processesForTable.sort((a, b) => a.label.localeCompare(b.label));
               setTableProcesses(processesForTable);

               const allProcesses = ProcessUtils.getProcessesForTable(metaData, tableName, true);
               setAllTableProcesses(allProcesses);
            }

            /////////////////////
            // load the record //
            /////////////////////
            let loadedRecord: QRecord = null;
            const newInitialValues: { [key: string]: any } = {};
            let defaultDisplayValues = new Map<string, string>();

            if (recordId)
            {
               let queryJoins: QueryJoin[] = null;
               const visibleJoinTables = TableUtils.getVisibleJoinTables(tableMetaData);
               if (visibleJoinTables.size > 0)
               {
                  queryJoins = TableUtils.getQueryJoins(tableMetaData, visibleJoinTables);
               }

               try
               {
                  loadedRecord = await qController.get(tableName, recordId, tableVariant, null, queryJoins);
                  setRecord(loadedRecord);

                  if (!skipNavigation)
                  {
                     const effectiveModeForHeader: RecordScreenMode = initialMode ?? (recordId ? "view" : "create");
                     setPageHeader(effectiveModeForHeader === "edit"
                        ? `Edit ${tableMetaData?.label}: ${loadedRecord.recordLabel}`
                        : loadedRecord.recordLabel);
                     recordAnalytics({category: "tableEvents", action: mode === "view" ? "view" : (isCopy ? "copy" : "edit"), label: tableMetaData?.label + " / " + loadedRecord?.recordLabel});

                     try
                     {
                        ////////////////////////////////////////////////////////////////////////////////////
                        // build the canonical record-view path from metadata + recordId, NOT from        //
                        // location.pathname.  the user may have arrived here via /order/5/edit,          //
                        // order/5/someProcess, or /order/5#audit - all of which refer to the same record //
                        // but would store as separate entries if we used pathname.  fall back to the     //
                        // pathname-based scrub only if metadata can't tell us the canonical path.        //
                        ////////////////////////////////////////////////////////////////////////////////////
                        const tablePath = metaData?.getTablePath(tableMetaData);
                        const viewPath = tablePath ? `${tablePath}/${recordId}` : location.pathname.replace(/\/edit\/?$/, "");
                        HistoryUtils.push({label: `${tableMetaData?.label}: ${loadedRecord.recordLabel}`, path: viewPath, iconName: tableMetaData.iconName});
                     }
                     catch (e)
                     {
                        console.error("Error pushing history: " + e);
                     }
                  }

                  /////////////////////////////////////////
                  // populate initial values from record //
                  /////////////////////////////////////////
                  tableMetaData.fields.forEach((fieldMetaData, key) =>
                  {
                     if (isCopy && fieldMetaData.name === tableMetaData.primaryKeyField)
                     {
                        return;
                     }
                     newInitialValues[key] = loadedRecord.values.get(key);
                  });

                  ///////////////////////
                  // permission checks //
                  ///////////////////////
                  if (mode === "edit" && !isCopy)
                  {
                     if (!tableMetaData.capabilities.has(Capability.TABLE_UPDATE))
                     {
                        setNotAllowedError("Records may not be edited in this table");
                     }
                     else if (!tableMetaData.editPermission)
                     {
                        setNotAllowedError(`You do not have permission to edit ${tableMetaData.label} records`);
                     }
                  }
               }
               catch (e)
               {
                  ////////////////////////////////////////////////////////////////////////////////
                  // the record couldn't be loaded (not-found, not-allowed, or a server error). //
                  // clear the stale record/header from a previously-viewed record so its label //
                  // doesn't leak, then show a message explaining why the record isn't shown.   //
                  ////////////////////////////////////////////////////////////////////////////////
                  clearStaleRecordAndPageHeader();

                  const status = e instanceof QException ? (e as QException).status : null;
                  if (status === 404)
                  {
                     setNotFoundMessage(`${tableMetaData.label} ${recordId} could not be found.`);
                  }
                  else if (status === 403)
                  {
                     setNotFoundMessage(`You do not have permission to view ${tableMetaData.label} records`);
                  }
                  else
                  {
                     console.error("Error loading record:", e);
                     setNotFoundMessage(`${tableMetaData.label} ${recordId} could not be loaded.`);
                  }

                  /////////////////////////////////////////////////////////////////////////
                  // for not-found / not-allowed, scrub the path from recently-viewed    //
                  // history; leave a (possibly-transient) server error's path in place. //
                  /////////////////////////////////////////////////////////////////////////
                  if (status === 404 || status === 403)
                  {
                     try
                     {
                        HistoryUtils.ensurePathNotInHistory(location.pathname);
                     }
                     catch (he)
                     {
                        console.error(he);
                     }
                  }

                  setLoading(false);
                  return;
               }
            }
            else
            {
               //////////////////////////////////////////////////////////////
               // create mode — clear any record from a previous view/edit //
               //////////////////////////////////////////////////////////////
               setRecord(null);
               if (!skipNavigation)
               {
                  setPageHeader(`Creating New ${tableMetaData?.label}`);
                  recordAnalytics({category: "tableEvents", action: "new", label: tableMetaData?.label});
               }

               ///////////////////////
               // populate defaults //
               ///////////////////////
               Object.assign(newInitialValues, DynamicFormUtils.getFieldDefaultValues(tableMetaData));

               ////////////////////////////////////////////////////////////////////////
               // merge in caller-supplied default values (e.g., from parent record) //
               ////////////////////////////////////////////////////////////////////////
               if (options?.defaultValues)
               {
                  for (let key in options.defaultValues)
                  {
                     newInitialValues[key] = options.defaultValues[key];
                  }
               }

               /////////////////////////////////////////////////////////
               // look up display values for PVS fields with defaults //
               /////////////////////////////////////////////////////////
               for (let fieldName of tableMetaData.fields.keys())
               {
                  const fieldMetaData = tableMetaData.fields.get(fieldName);
                  const defaultValue = newInitialValues[fieldName];
                  if (defaultValue && fieldMetaData.possibleValueSourceName)
                  {
                     const {results} = await qController.possibleValues(tableName, null, fieldName, null, [defaultValue], null, objectToMap(newInitialValues), "form");
                     if (results && results.length > 0)
                     {
                        defaultDisplayValues.set(fieldName, results[0].label);
                     }
                  }
               }

               //////////////////////////////////
               // permission checks for create //
               //////////////////////////////////
               if (!tableMetaData.capabilities.has(Capability.TABLE_INSERT))
               {
                  setNotAllowedError("Records may not be created in this table");
               }
               else if (!tableMetaData.insertPermission)
               {
                  setNotAllowedError(`You do not have permission to create ${tableMetaData.label} records`);
               }
            }

            /////////////////////////////////////////////
            // run on-load form adjuster if applicable //
            /////////////////////////////////////////////
            const materialDashboardTableMetaData = tableMetaData.supplementalTableMetaData?.get("materialDashboard");
            if (materialDashboardTableMetaData?.onLoadFormAdjuster)
            {
               /////////////////////////////////////////////////////////////////////////////////
               // the outer formAdjusterCallbacks closes over the React-state tableMetaData,  //
               // which here is still the previous value (setTableMetaData hasn't fired yet). //
               // override the two tableMetaData-using callbacks to use the local, freshly-   //
               // loaded tableMetaData from line 315.                                         //
               /////////////////////////////////////////////////////////////////////////////////
               const loadCallbacks: FormAdjusterCallbacks = {
                  ...formAdjusterCallbacks,
                  applyUpdatedSections: (response) => applyUpdatedSectionsFromFormAdjuster(tableMetaData, response),
                  disableAllFields: () => disableAllFields(tableMetaData),
               };
               await runOnLoadFormAdjuster(tableMetaData, newInitialValues, defaultDisplayValues, loadCallbacks);
            }

            DynamicFormUtils.formatDateTimeValuesForForm(newInitialValues, tableMetaData);

            setInitialValues(newInitialValues);

            ////////////////////////////////////////////////////////////////////////////////
            // build sections                                                             //
            //                                                                            //
            // use effectiveMode (derived from props) instead of mode state, which may    //
            // be stale in this effect when both recordId and initialMode change together //
            ////////////////////////////////////////////////////////////////////////////////
            const effectiveMode: RecordScreenMode = initialMode ?? (recordId ? "view" : "create");
            const sections = buildSections(tableMetaData, metaData, effectiveMode);
            setTableSections(sections);

            const t1 = sections.find(s => s.tier === "T1");
            const nonT1 = sections.filter(s => s.tier !== "T1" && !s.isHidden);
            setT1Section(t1);
            setNonT1Sections(nonT1);

            figureOutSectionVisibility(tableMetaData);

            ////////////////////////
            // collapsible states //
            ////////////////////////
            const initialCollapsibleStates: Record<string, boolean> = {};
            for (let section of sections)
            {
               if (section.collapsible?.isCollapsible)
               {
                  const lsValue = localStorage.getItem(makeCollapsibleSectionOpenStateLocalStorageKey(tableName, section.name));
                  initialCollapsibleStates[section.name] = lsValue != undefined ? lsValue === "true" : section.collapsible.initiallyOpen === true;
               }
               else
               {
                  initialCollapsibleStates[section.name] = true;
               }
            }
            setCollapsibleSectionOpenStates(initialCollapsibleStates);

            ///////////////////////////
            // build form field data //
            ///////////////////////////
            const {dynamicFormFields, formValidations: fv} = DynamicFormUtils.buildDynamicFormFields(tableMetaData, loadedRecord ? loadedRecord.displayValues : defaultDisplayValues, disabledFields);

            setFormFields(dynamicFormFields);
            setFormValidations(fv);

            ///////////////////////////////////////////////////////////////////////////////////////
            // the set of adjuster-replaced names describes the validations just replaced above, //
            // so it goes with them.  RecordScreen is routed without a key, so navigating from   //
            // one record to another re-runs this effect on the SAME hook instance - and a name  //
            // left over from record 1 would permanently shadow a widget's registration for that //
            // field on record 2, where no adjuster ever ran.                                    //
            ///////////////////////////////////////////////////////////////////////////////////////
            adjusterReplacedFieldNames.current = new Set();

            rebuildFormFieldsBySection(sections, dynamicFormFields);

            /////////////////////////
            // read location state //
            /////////////////////////
            if (!skipNavigation && location.state)
            {
               let state: any = location.state;
               if (state["createSuccess"] || state["updateSuccess"])
               {
                  setSuccessMessage(`${tableMetaData.label} successfully ${state["createSuccess"] ? "created" : "updated"}`);
               }
               if (state["warning"])
               {
                  setWarningMessage(state["warning"]);
               }
               delete state["createSuccess"];
               delete state["updateSuccess"];
               delete state["warning"];
               window.history.replaceState(state, "");
            }
         }
         catch (e)
         {
            console.error("Error loading record screen data:", e);
            setErrorMessage(`Error loading data: ${e instanceof Error ? e.message : String(e)}`);
         }
         finally
         {
            setLoading(false);
         }
      })();
   }, [tableName, recordId, dataLoadCounter]);


   /***************************************************************************
    ** Switch mode and rebuild sections synchronously in one batch, so there's
    ** no intermediate render with new mode + old sections (which would cause
    ** view-only widget-adorned fields to error in edit mode).
    **
    ** viewReloadValues - when switching to view after an in-place save, the
    ** caller passes the freshly-saved (raw, backend-formatted) record values, to
    ** re-run the on-load form adjuster against them.  When omitted (e.g. cancel
    ** out of edit), the current initialValues are used.
    ***************************************************************************/
   function switchMode(newMode: RecordScreenMode, viewReloadValues?: { [key: string]: any }): void
   {
      ////////////////////////////////////////////////////////////////////////////////
      // update the ref immediately so async callbacks (form adjuster round-trips,  //
      // loadTableMetaData .then() blocks) read the current mode, not a stale value //
      // captured in their closure.  The useEffect that syncs the ref runs after    //
      // render and would be too late for in-flight async operations.               //
      ////////////////////////////////////////////////////////////////////////////////
      modeRef.current = newMode;
      setMode(newMode);

      //////////////////////////////////////////////////////////////////////////////////////
      // when leaving edit mode, clear form-disabled state (set by form adjuster)         //
      // and restore field editability by re-cloning from the cached (unmutated) metadata //
      // (disableAllFields mutates field.isEditable on the clone)                         //
      //////////////////////////////////////////////////////////////////////////////////////
      if (newMode === "view")
      {
         setNotAllowedError(null);

         ////////////////////////////////////////////////////////////////////////////////////
         // the save path already refreshed every widget on the screen, so the adjuster    //
         // below must not ask for those same widgets again.  read-and-clear               //
         // SYNCHRONOUSLY, here, before any promise: every path out of switchMode has to   //
         // clear this, or a rejected loadTableMetaData (or either guard inside its .then) //
         // would leave it set for the life of the screen, silently dropping every later   //
         // reloadWidgets call.  a view transition that is not a save (cancel out of edit) //
         // reads false, and so still honors the adjuster.                                 //
         ////////////////////////////////////////////////////////////////////////////////////
         const saveAlreadyRefetchedWidgets = saveScheduledWidgetRefetchRef.current;
         saveScheduledWidgetRefetchRef.current = false;

         if (tableName)
         {
            qController.loadTableMetaData(tableName).then(async cached =>
            {
               //////////////////////////////////////////////////////////////////////////////
               // guard: if mode has already changed away from view (e.g. the user clicked //
               // Edit before this metadata fetch resolved), skip the whole view rebuild - //
               // don't touch metadata or sections.                                        //
               //////////////////////////////////////////////////////////////////////////////
               if (modeRef.current !== "view")
               {
                  return;
               }

               ///////////////////////////////////////////////////////////////////////////////
               // guard: if there are no values, the record has not loaded yet (this call   //
               // came from the initial-mount useEffect before the record data arrived      //
               // — running the adjuster with empty values would fall into the wrong branch //
               // and corrupt section state).  Skip; the main load will run the adjuster.   //
               ///////////////////////////////////////////////////////////////////////////////
               const valuesForAdjuster = viewReloadValues ?? initialValues;
               if (Object.keys(valuesForAdjuster).length === 0)
               {
                  return;
               }

               const freshTable = cached.clone();

               /////////////////////////////////////////////////////////////////////////////////
               // re-run the on-load form adjuster in view mode, and rebuild view sections    //
               // from the adjusted metadata - mirroring the main data-load effect - so that  //
               // section overrides (field reordering, section visibility) survive the return //
               // from edit, both on cancel and after an in-place save (where the record's    //
               // data just changed, so the adjustments must reflect the saved state without  //
               // a full page reload).                                                        //
               //                                                                             //
               // override the tableMetaData-using callbacks to use the freshly-cloned local  //
               // copy, so they don't close over stale React state.                           //
               //                                                                             //
               // viewReloadValues must be the raw (timezone-qualified) backend values, same  //
               // as the main data-load effect feeds the adjuster, so DATE_TIME fields carry  //
               // an accurate instant instead of a naive local-zone picker string.            //
               /////////////////////////////////////////////////////////////////////////////////
               const materialDashboardTableMetaData = freshTable.supplementalTableMetaData?.get("materialDashboard");
               if (materialDashboardTableMetaData?.onLoadFormAdjuster)
               {
                  const viewCallbacks: FormAdjusterCallbacks = {
                     ...formAdjusterCallbacks,
                     applyUpdatedSections: (response) => applyUpdatedSectionsFromFormAdjuster(freshTable, response, "view"),
                     disableAllFields: () => disableAllFields(freshTable),
                     shouldApplyFormDisabled: () => false,
                     reloadWidgets: (widgetNames) =>
                     {
                        if (saveAlreadyRefetchedWidgets)
                        {
                           return;
                        }

                        for (const widgetName of widgetNames)
                        {
                           refetchWidgetInPlace(widgetName);
                        }
                     },
                  };

                  /////////////////////////////////////////////////////////////////////////////
                  // guard the adjuster: if it throws, still fall through to set the fresh   //
                  // metadata and rebuild sections below, so a failed adjuster doesn't leave //
                  // the screen stranded on stale metadata after save.                       //
                  /////////////////////////////////////////////////////////////////////////////
                  try
                  {
                     await runOnLoadFormAdjuster(freshTable, valuesForAdjuster, new Map(), viewCallbacks, () => modeRef.current === "view");
                  }
                  catch (e)
                  {
                     console.error("Error running on-load form adjuster on view transition", e);
                  }

                  ///////////////////////////////////////////////////////////////////////////
                  // re-check the mode after the adjuster's network round-trip: the user   //
                  // may have clicked Edit while it was in flight, in which case           //
                  // switchMode("edit") already rebuilt edit-mode sections - don't clobber //
                  // them with view state.                                                 //
                  ///////////////////////////////////////////////////////////////////////////
                  if (modeRef.current !== "view")
                  {
                     return;
                  }

                  setTableMetaData(freshTable);

                  const sections = buildSections(freshTable, metaData, "view");
                  setTableSections(sections);
                  setT1Section(sections.find(s => s.tier === "T1"));
                  setNonT1Sections(sections.filter(s => s.tier !== "T1" && !s.isHidden));
                  rebuildFormFieldsBySection(sections);
               }
               else
               {
                  setTableMetaData(freshTable);
               }

               figureOutSectionVisibility(freshTable);
            });
         }
      }

      if (tableMetaData && metaData)
      {
         const newSections = buildSections(tableMetaData, metaData, newMode);

         /////////////////////////////////////////////////////////////////////////
         // preserve object identity for widget sections that already exist, so //
         // that the memo on RecordScreenSection can skip re-renders for        //
         // unchanged widget sections (avoiding flicker on view <-> edit        //
         // transitions). Only preserve widget sections; field sections may     //
         // have mode-dependent alternatives that need the new version.         //
         /////////////////////////////////////////////////////////////////////////
         const prevSectionsByName = new Map(tableSections.map(s => [s.name, s]));
         const sections = newSections.map(s =>
         {
            if (s.widgetName)
            {
               return prevSectionsByName.get(s.name) ?? s;
            }
            return s;
         });

         setTableSections(sections);

         const t1 = sections.find(s => s.tier === "T1");
         const nonT1 = sections.filter(s => s.tier !== "T1" && !s.isHidden);
         setT1Section(t1);
         setNonT1Sections(nonT1);
         rebuildFormFieldsBySection(sections);

         /////////////////////////////////////////////////////////////////////////////////
         // when entering edit mode, run the on-load form adjuster if one is configured //
         /////////////////////////////////////////////////////////////////////////////////
         if (newMode === "edit")
         {
            const materialDashboardTableMetaData = tableMetaData.supplementalTableMetaData?.get("materialDashboard");
            if (materialDashboardTableMetaData?.onLoadFormAdjuster)
            {
               const localCallbacks: FormAdjusterCallbacks = {
                  ...formAdjusterCallbacks,
                  shouldApplyFormDisabled: () => newMode === "edit" || newMode === "create",
               };
               runOnLoadFormAdjuster(tableMetaData, initialValues, new Map(), localCallbacks);
            }
         }
      }
   }


   /***************************************************************************
    ** build sections for the sidebar & page layout
    ***************************************************************************/
   function buildSections(tableMetaData: QTableMetaData, metaData: QInstance, mode: RecordScreenMode): QTableSection[]
   {
      const alternativeType = mode === "view" ? "RECORD_VIEW" : undefined;

      if (mode === "view")
      {
         return TableUtils.getSectionsForRecordSidebar(tableMetaData, null, undefined, alternativeType);
      }
      else if (mode === "create")
      {
         //////////////////////////////////////////////////
         // create: only include qualifying widget types //
         //////////////////////////////////////////////////
         return TableUtils.getSectionsForRecordSidebar(tableMetaData, [...tableMetaData.fields.keys()], (section: QTableSection) =>
         {
            const widget = metaData?.widgets?.get(section.widgetName);
            if (widget)
            {
               if (widget.type === "childRecordList" && widget.defaultValues?.has("manageAssociationName"))
               {
                  return true;
               }
               if (widget.type === "filterAndColumnsSetup" || widget.type === "pivotTableSetup" || widget.type === "dynamicForm")
               {
                  return true;
               }
               if (widget.defaultValues?.get("includeOnRecordEditScreen"))
               {
                  return true;
               }
            }
            return false;
         });
      }
      else
      {
         ////////////////////////////////////////////////////////////////////////
         // edit: include all widget sections (so the layout doesn't jump) and //
         // sections containing only join fields (they render read-only rather //
         // than disappearing, which avoids layout jumps too)                  //
         ////////////////////////////////////////////////////////////////////////
         return TableUtils.getSectionsForRecordSidebar(tableMetaData, [...tableMetaData.fields.keys()], (section: QTableSection) =>
         {
            if (section.widgetName)
            {
               return true;
            }
            if (section.fieldNames?.some(fn => fn.includes(".")))
            {
               return true;
            }
            return false;
         });
      }
   }


   /***************************************************************************
    ** rebuild the formFieldsBySection map from the given sections.
    ** If allFormFields is not provided, uses the current formFields state.
    ***************************************************************************/
   function rebuildFormFieldsBySection(sections: QTableSection[], allFormFields?: { [key: string]: DynamicFormFieldDefinition }): void
   {
      const fields = allFormFields ?? formFieldsRef.current;
      const newFormFieldsBySection = new Map<string, DynamicFormFieldDefinition[]>();

      for (let section of sections)
      {
         if (section.isHidden || !section.fieldNames || section.fieldNames.length === 0)
         {
            continue;
         }

         const sectionFields: DynamicFormFieldDefinition[] = [];
         for (let fieldName of section.fieldNames)
         {
            const formField = fields[fieldName];
            if (formField)
            {
               sectionFields.push(formField);
            }
         }

         if (sectionFields.length > 0)
         {
            newFormFieldsBySection.set(section.name, sectionFields);
         }
      }

      setFormFieldsBySection(newFormFieldsBySection);
   }


   /***************************************************************************
    ** When entering edit mode, load widget data for managed-association
    ** childRecordList widgets so they can be rendered inline and managed.
    ***************************************************************************/
   useEffect(() =>
   {
      if ((mode === "edit" || mode === "create") && tableMetaData && metaData)
      {
         (async () =>
         {
            const newChildListWidgetData: { [name: string]: ChildRecordListData } = {};

            for (let section of tableSections)
            {
               if (!section.widgetName) continue;

               const widget = metaData.widgets.get(section.widgetName);
               if (!widget || widget.type !== "childRecordList") continue;
               if (!widget.defaultValues?.has("manageAssociationName")) continue;

               try
               {
                  const widgetFormData = new FormData();
                  widgetFormData.append("tableName", tableMetaData.name);
                  if (recordId)
                  {
                     widgetFormData.append("id", recordId);
                  }
                  const data = await qController.widget(widget.name, widgetFormData);
                  newChildListWidgetData[widget.name] = data as ChildRecordListData;
               }
               catch (e)
               {
                  console.error(`Error loading widget ${widget.name} for ${mode} mode:`, e);
               }
            }

            setChildListWidgetData(newChildListWidgetData);
         })();
      }
      else if (mode === "view")
      {
         setChildListWidgetData({});
      }
   }, [mode, tableSections]);


   /***************************************************************************
    ** Open the add-child-record modal for a managed-association widget.
    ***************************************************************************/
   function openAddChildRecord(widgetName: string, widgetData: ChildRecordListData): void
   {
      let defaultValues = widgetData.defaultValuesForNewChildRecords || {};

      let disabledFields = widgetData.disabledFieldsForNewChildRecords;
      if (!disabledFields)
      {
         disabledFields = widgetData.defaultValuesForNewChildRecords;
      }

      if (widgetData.defaultValuesForNewChildRecordsFromParentFields)
      {
         for (let childField in widgetData.defaultValuesForNewChildRecordsFromParentFields)
         {
            const parentField = widgetData.defaultValuesForNewChildRecordsFromParentFields[childField];
            defaultValues[childField] = formValuesRef.current[parentField];
         }
      }

      setShowEditChildForm({widgetName, table: resolveChildTableMetaData(widgetData), rowIndex: null, defaultValues, disabledFields});
   }


   /***************************************************************************
    ** Open the edit-child-record modal for an existing row.
    ***************************************************************************/
   function openEditChildRecord(widgetName: string, widgetData: ChildRecordListData, rowIndex: number): void
   {
      let defaultValues = widgetData.queryOutput.records[rowIndex].values;

      let disabledFields = widgetData.disabledFieldsForNewChildRecords;
      if (!disabledFields)
      {
         disabledFields = widgetData.defaultValuesForNewChildRecords;
      }

      setShowEditChildForm({widgetName, table: resolveChildTableMetaData(widgetData), rowIndex, defaultValues, disabledFields});
   }


   /***************************************************************************
    ** Delete a child record from the in-memory list.
    ***************************************************************************/
   function deleteChildRecord(widgetName: string, rowIndex: number): void
   {
      const newData = Object.assign({}, childListWidgetData);
      if (!newData[widgetName]?.queryOutput?.records) return;

      newData[widgetName].queryOutput.records.splice(rowIndex, 1);
      newData[widgetName].totalRows = newData[widgetName].queryOutput.records.length;
      setChildListWidgetData({...newData});
   }


   /***************************************************************************
    ** Called when the modal child-record form is submitted.
    ***************************************************************************/
   async function submitEditChildForm(values: any, tableName: string): Promise<void>
   {
      const widgetName = showEditChildForm.widgetName;
      const action: "insert" | "edit" = showEditChildForm.rowIndex == null ? "insert" : "edit";
      const rowIndex = showEditChildForm.rowIndex;

      const newData: { [name: string]: ChildRecordListData } = Object.assign({}, childListWidgetData);
      if (!newData[widgetName].queryOutput.records)
      {
         newData[widgetName].queryOutput.records = [];
      }

      /////////////////////////////////////////
      // build display values for PVS fields //
      /////////////////////////////////////////
      const displayValues: { [fieldName: string]: string } = {};
      if (tableName && values)
      {
         const childTableMetaData = await qController.loadTableMetaData(tableName);
         for (let key in values)
         {
            const field = childTableMetaData.fields.get(key);
            if (field?.possibleValueSourceName)
            {
               const {results: possibleValues} = await qController.possibleValues(tableName, null, field.name, null, [values[key]], null, objectToMap(values), "form");
               if (possibleValues?.length > 0)
               {
                  displayValues[key] = possibleValues[0].label;
               }
            }
         }
      }

      switch (action)
      {
         case "insert":
            newData[widgetName].queryOutput.records.push({values, displayValues});
            break;
         case "edit":
            newData[widgetName].queryOutput.records[rowIndex] = {values, displayValues};
            break;
      }
      newData[widgetName].totalRows = newData[widgetName].queryOutput.records.length;
      setChildListWidgetData({...newData});
   }


   /***************************************************************************
    ** Bulk-append pending child rows to a managed-association widget's
    ** in-memory list (the bulk-add-via-filter picker path on create/edit
    ** screens).  Mirrors submitEditChildForm's insert case - including the
    ** default-values merge that openAddChildRecord applies, and PVS display-
    ** value resolution - but batched: one possibleValues call per PVS field
    ** (with all the rows' distinct ids) instead of one per row.
    ***************************************************************************/
   async function bulkAppendChildRecords(widgetName: string, rowsValues: { [fieldName: string]: any }[]): Promise<void>
   {
      if (!rowsValues || rowsValues.length == 0)
      {
         return;
      }

      const newData: { [name: string]: ChildRecordListData } = Object.assign({}, childListWidgetData);
      if (!newData[widgetName])
      {
         return;
      }
      if (!newData[widgetName].queryOutput)
      {
         newData[widgetName].queryOutput = {records: []};
      }
      if (!newData[widgetName].queryOutput.records)
      {
         newData[widgetName].queryOutput.records = [];
      }

      ///////////////////////////////////////////////////////////////////////////
      // merge in the same default values that the add-new path                //
      // (openAddChildRecord) seeds its form with - so bulk-added pending rows //
      // come out shaped just like hand-added ones.                            //
      ///////////////////////////////////////////////////////////////////////////
      const defaultValues = {...(newData[widgetName].defaultValuesForNewChildRecords || {})};
      if (newData[widgetName].defaultValuesForNewChildRecordsFromParentFields)
      {
         for (let childField in newData[widgetName].defaultValuesForNewChildRecordsFromParentFields)
         {
            const parentField = newData[widgetName].defaultValuesForNewChildRecordsFromParentFields[childField];
            defaultValues[childField] = formValuesRef.current[parentField];
         }
      }

      const mergedRows = rowsValues.map(rowValues => ({...defaultValues, ...rowValues}));

      /////////////////////////////////////////////////////////////////////////////////
      // build display values for PVS fields - rows are homogeneous, so iterate the  //
      // first row's fields, and batch the possibleValues calls per PVS field with   //
      // the rows' distinct ids, building an id -> label map to apply per row.  the  //
      // backend caps each possibleValues response at 250 (even with an id list), so //
      // chunk the ids at 250 per call and merge the resulting label maps.           //
      /////////////////////////////////////////////////////////////////////////////////
      const POSSIBLE_VALUES_PAGE_SIZE = 250;
      const labelsByFieldThenId: { [fieldName: string]: { [id: string]: string } } = {};
      const childTableName = resolveChildTableMetaData(newData[widgetName])?.name;
      if (childTableName)
      {
         const childTableMetaData = await qController.loadTableMetaData(childTableName);
         for (let key in mergedRows[0])
         {
            const field = childTableMetaData.fields.get(key);
            if (field?.possibleValueSourceName)
            {
               const distinctIds = [...new Set(mergedRows.map(row => row[key]).filter(value => value != null))];
               if (distinctIds.length > 0)
               {
                  const labelsById: { [id: string]: string } = {};
                  for (let chunkStart = 0; chunkStart < distinctIds.length; chunkStart += POSSIBLE_VALUES_PAGE_SIZE)
                  {
                     const idsChunk = distinctIds.slice(chunkStart, chunkStart + POSSIBLE_VALUES_PAGE_SIZE);
                     //////////////////////////////////////////////////////////////////////////////////
                     // row 0's values stand as the context for every row.  These rows are           //
                     // homogeneous - the bulk-add picker produces one field per row, differing only //
                     // in the picked id - and building a context per row would mean one call per    //
                     // row, which is exactly the batching this function exists to avoid.  A         //
                     // possible-value source whose filter depends on ANOTHER row's value would need //
                     // that per-row context; none reachable from this path does.                    //
                     //////////////////////////////////////////////////////////////////////////////////
                     const {results: possibleValues} = await qController.possibleValues(childTableName, null, field.name, null, idsChunk, null, objectToMap(mergedRows[0]), "form");
                     for (let possibleValue of possibleValues ?? [])
                     {
                        labelsById[String(possibleValue.id)] = possibleValue.label;
                     }
                  }
                  labelsByFieldThenId[key] = labelsById;
               }
            }
         }
      }

      for (let values of mergedRows)
      {
         const displayValues: { [fieldName: string]: string } = {};
         for (let key in values)
         {
            const label = labelsByFieldThenId[key]?.[String(values[key])];
            if (label != null)
            {
               displayValues[key] = label;
            }
         }
         newData[widgetName].queryOutput.records.push({values, displayValues});
      }

      newData[widgetName].totalRows = newData[widgetName].queryOutput.records.length;
      setChildListWidgetData({...newData});
   }


   /***************************************************************************
    ** apply updatedSectionMetaData from a form adjuster response
    ***************************************************************************/
   function applyUpdatedSectionsFromFormAdjuster(table: QTableMetaData, response: any, modeOverride?: RecordScreenMode): void
   {
      const updatedSections: Record<string, QTableSection> = response?.updatedSectionMetaData;
      if (!updatedSections)
      {
         return;
      }

      for (let name in updatedSections)
      {
         for (let i = 0; i < (table.sections ?? []).length; i++)
         {
            if (table.sections[i].name === name)
            {
               table.sections[i] = new QTableSection(updatedSections[name]);
            }
         }
      }

      ////////////////////////////////////////////////////////////////////////////////
      // callers running outside of a settled `mode` (e.g. the post-save view       //
      // re-run, where `mode` is still "edit" in this closure) pass modeOverride so //
      // sections build for the correct mode.  otherwise use modeRef (not the       //
      // `mode` state), which switchMode updates synchronously, so an async         //
      // callback doesn't build sections for the mode we just left.                 //
      ////////////////////////////////////////////////////////////////////////////////
      const sections = buildSections(table, metaData, modeOverride ?? modeRef.current);
      setTableSections(sections);
      setNonT1Sections(sections.filter(s => s.tier !== "T1" && !s.isHidden));

      ////////////////////////////////////////////////////////////////////////////////////
      // A section hidden at form load is skipped by rebuildFormFieldsBySection, so its //
      // fields never get a form-field def.  When an adjuster reveals such a section,   //
      // rebuild the by-section map (from now-visible sections) so the newly-shown      //
      // fields resolve their defs instead of rendering "Field not configured".         //
      ////////////////////////////////////////////////////////////////////////////////////
      rebuildFormFieldsBySection(sections);
   }


   /***************************************************************************
    ** collapsible section helpers
    ***************************************************************************/
   function makeCollapsibleSectionOpenStateLocalStorageKey(tableName: string, sectionName: string): string
   {
      return `qqq.recordView.collapsibleSectionOpenStates.${tableName}.${sectionName}`;
   }

   function toggleCollapsibleSectionOpenState(sectionName: string): void
   {
      const newValue = !collapsibleSectionOpenStates[sectionName];
      setCollapsibleSectionOpenStates((prevState) => ({...prevState, [sectionName]: newValue}));
      localStorage.setItem(makeCollapsibleSectionOpenStateLocalStorageKey(tableName, sectionName), newValue.toString());
   }


   /***************************************************************************
    ** Compute per-section visibility: a field-based section is hidden if all
    ** its fields are hidden. Widget sections are always visible (unless the
    ** section itself is hidden, which is handled separately).
    ***************************************************************************/
   /////////////////////////////////////////////////////////////////////////////////////
   // disable all fields on the table — used when a form adjuster says isFormDisabled //
   /////////////////////////////////////////////////////////////////////////////////////
   function disableAllFields(table: QTableMetaData): void
   {
      table.fields?.forEach((field) => field.isEditable = false);
      for (let fn in formFields)
      {
         formFields[fn].isEditable = false;
      }
      setFormFields({...formFields});
   }


   function figureOutSectionVisibility(table: QTableMetaData): void
   {
      const newVisibility: { [key: string]: boolean } = {};
      for (const section of table.sections ?? [])
      {
         if (section.widgetName || !section.fieldNames?.length)
         {
            continue;
         }

         let anyVisible = false;
         for (const fieldName of section.fieldNames)
         {
            const [field] = TableUtils.getFieldAndTable(table, fieldName);
            if (field && !field.isHidden)
            {
               anyVisible = true;
               break;
            }
         }

         newVisibility[section.name] = anyVisible;
      }
      setSectionVisibility(newVisibility);
   }


   /***************************************************************************
    ** Open any collapsed sections that contain fields with validation errors.
    ***************************************************************************/
   function openCollapsedSectionsWithErrorFields(errorFieldNames: string[]): void
   {
      const updates: Record<string, boolean> = {};
      for (const section of nonT1Sections)
      {
         if (section.fieldNames && collapsibleSectionOpenStates[section.name] === false)
         {
            if (section.fieldNames.some((fn: string) => errorFieldNames.includes(fn)))
            {
               updates[section.name] = true;
            }
         }
      }
      if (Object.keys(updates).length > 0)
      {
         setCollapsibleSectionOpenStates((prev) => ({...prev, ...updates}));
      }
   }


   /***************************************************************************
    ** Notify that the form's field defs changed after an on-change form
    ** adjuster ran.  RecordScreenField renders from formFieldsBySection (not
    ** formFields), so a bare setFormFields would leave the rendered field defs
    ** stale — an adjuster that changes a field's metadata or possible-value
    ** display value would not show.  Rebuild formFieldsBySection from the
    ** just-mutated formFields too.
    ***************************************************************************/
   function notifyFormFieldsChanged(): void
   {
      setFormFields({...formFields});
      rebuildFormFieldsBySection(tableSections, formFields);
   }


   /***************************************************************************
    ** When an on-change form adjuster replaces a field's metadata, rebuild
    ** that field's entry in formValidations.  The Yup schema is derived from
    ** field metadata at load time and doesn't track later formFields
    ** mutations — without this, an adjuster-driven isRequired change never
    ** reaches validation, so e.g. a field the adjuster un-required (and hid)
    ** would still silently block submit.
    ***************************************************************************/
   function handleFieldMetaDataReplaced(fieldName: string, updatedField: QFieldMetaData): void
   {
      adjusterReplacedFieldNames.current.add(fieldName);
      setFormValidations(prev => ({...prev, [fieldName]: DynamicFormUtils.getValidationForField(updatedField, disabledFields)}));
   }


   /***************************************************************************
    ** handle field-rule evaluation for a changed field value
    ***************************************************************************/
   function handleFieldChange(fieldName: string, oldValue: any, newValue: any, valueChangesToMake: { [fieldName: string]: any }): void
   {
      evaluateFieldRules(fieldRules, fieldName, newValue, valueChangesToMake, reloadWidget);

      ///////////////////////////////////////////
      // on-change form adjuster (field-level) //
      ///////////////////////////////////////////
      const field = formFields[fieldName];
      if (field)
      {
         const materialDashboardFieldMetaData = field.fieldMetaData?.supplementalFieldMetaData?.get("materialDashboard");
         if (materialDashboardFieldMetaData?.onChangeFormAdjuster)
         {
            if (field.type === "file" || field.type === "checkbox" || field.possibleValueProps)
            {
               runOnChangeFormAdjuster({
                  tableName,
                  table: tableMetaData,
                  fieldName,
                  newValue,
                  materialDashboardFieldMetaData,
                  formFields,
                  formValuesRef,
                  setFieldValueRef,
                  onFormFieldsChanged: notifyFormFieldsChanged,
                  callbacks: onChangeFormAdjusterCallbacks,
                  onFieldMetaDataReplaced: handleFieldMetaDataReplaced,
                  disabledFields,
               });
            }
         }
      }
   }


   //////////////////////////////////////////////////////////////////////////////
   // handle blur on a text field — runs on-change form adjuster if configured //
   //////////////////////////////////////////////////////////////////////////////
   function handleFieldBlur(fieldName: string, value: any): void
   {
      const field = formFields[fieldName];
      if (field)
      {
         const materialDashboardFieldMetaData = field.fieldMetaData?.supplementalFieldMetaData?.get("materialDashboard");
         if (materialDashboardFieldMetaData?.onChangeFormAdjuster)
         {
            runOnChangeFormAdjuster({
               tableName,
               table: tableMetaData,
               fieldName,
               newValue: value,
               materialDashboardFieldMetaData,
               formFields,
               formValuesRef,
               setFieldValueRef,
               onFormFieldsChanged: notifyFormFieldsChanged,
               callbacks: onChangeFormAdjusterCallbacks,
               onFieldMetaDataReplaced: handleFieldMetaDataReplaced,
               disabledFields,
            });
         }
      }
   }


   /***************************************************************************
    ** trigger a widget reload by incrementing the counter (which changes the
    ** key on DashboardWidgets, forcing a remount and fresh data fetch).
    ** Additional params are stored so the widget can include them in its fetch.
    ***************************************************************************/
   function reloadWidget(widgetName: string, additionalParams?: { [key: string]: any }): void
   {
      if (additionalParams && widgetName)
      {
         setWidgetReloadAdditionalParams(prev => ({...prev, [widgetName]: additionalParams}));
      }
      ///////////////////////////////////////////////////////////////////////
      // increment per-widget counter (or "__all__" to reload all widgets) //
      ///////////////////////////////////////////////////////////////////////
      const key = widgetName ?? "__all__";
      setWidgetReloadCounters(prev => ({...prev, [key]: (prev[key] ?? 0) + 1}));
   }


   /***************************************************************************
    ** Refresh a widget's data IN PLACE (no remount, no blank) by bumping its
    ** per-widget signal.  Used by the post-save on-load adjuster path to reflect
    ** freshly-saved values without the layout jump a remount would cause.
    ***************************************************************************/
   function refetchWidgetInPlace(widgetName: string): void
   {
      setWidgetRefetchSignals(prev => ({...prev, [widgetName]: (prev[widgetName] ?? 0) + 1}));
   }


   /***************************************************************************
    ** force a full re-fetch of record data (e.g., from #reload hash)
    ***************************************************************************/
   function reloadRecordData(): void
   {
      setDataLoadCounter(c => c + 1);
   }


   function bundleAssociationsIntoPayload(valuesToPost: any): void
   {
      const associationsToPost: any = {};
      let haveAssociationsToPost = false;
      for (let widgetName of Object.keys(childListWidgetData))
      {
         const manageAssociationName = metaData?.widgets?.get(widgetName)?.defaultValues?.get("manageAssociationName");
         if (!manageAssociationName) continue;

         if (childListWidgetData[widgetName]?.queryOutput?.records)
         {
            associationsToPost[manageAssociationName] = [];
            haveAssociationsToPost = true;
            for (let rec of childListWidgetData[widgetName].queryOutput.records)
            {
               associationsToPost[manageAssociationName].push(rec.values);
            }
         }
      }
      if (haveAssociationsToPost)
      {
         valuesToPost["associations"] = JSON.stringify(associationsToPost);
      }
   }


   /***************************************************************************
    ** save the record (update or create)
    ***************************************************************************/
   async function saveRecord(values: any): Promise<QRecord>
   {
      /////////////////////////////////////////////////////////////////////
      // if an onSubmitCallback is provided, use it instead of API calls //
      /////////////////////////////////////////////////////////////////////
      if (options?.onSubmitCallback)
      {
         options.onSubmitCallback(values, tableName);
         return (null);
      }

      const valuesToPost = DynamicFormUtils.sanitizeFormValuesForSave(values, tableMetaData, initialValues);

      const pathParts = location.pathname.replace(/\/+$/, "").split("/");

      if (recordId && !isCopy)
      {
         ////////////
         // update //
         ////////////
         if (!skipNavigation)
         {
            recordAnalytics({category: "tableEvents", action: "saveEdit", label: tableMetaData?.label});
         }

         bundleAssociationsIntoPayload(valuesToPost);

         const updatedRecord = await qController.update(tableName, recordId, valuesToPost);

         if (skipNavigation)
         {
            /////////////////////////////////////////////////////////////
            // modal context — don't refetch, navigate, or change mode //
            /////////////////////////////////////////////////////////////
            return (updatedRecord);
         }

         /////////////////////////////////////////////////////////////////
         // re-fetch the full record (with display values, joins, etc.) //
         /////////////////////////////////////////////////////////////////
         let queryJoins: QueryJoin[] = null;
         const visibleJoinTables = TableUtils.getVisibleJoinTables(tableMetaData);
         if (visibleJoinTables.size > 0)
         {
            queryJoins = TableUtils.getQueryJoins(tableMetaData, visibleJoinTables);
         }
         const freshRecord = await qController.get(tableName, recordId, tableVariant, null, queryJoins);
         setRecord(freshRecord);
         setPageHeader(freshRecord.recordLabel);

         ////////////////////////////////////////////////////////////////////////////////////////////////
         // update initialValues so re-entering edit mode has fresh data.                              //
         // adjusterValues keeps the raw (timezone-qualified) backend values - these go to             //
         // the post-save on-load adjuster (via switchMode), matching the initial-load path,           //
         // where the adjuster runs on raw backend values before formatDateTimeValuesForForm.          //
         // newInitialValues gets the form-formatted (naive local) DATE_TIME strings the picker wants. //
         ////////////////////////////////////////////////////////////////////////////////////////////////
         const newInitialValues: { [key: string]: any } = {};
         const adjusterValues: { [key: string]: any } = {};
         tableMetaData.fields.forEach((fieldMetaData, key) =>
         {
            const rawValue = freshRecord.values.get(key);
            adjusterValues[key] = rawValue;
            newInitialValues[key] = rawValue;
            if (fieldMetaData.type === QFieldType.DATE_TIME && newInitialValues[key])
            {
               newInitialValues[key] = ValueUtils.formatDateTimeValueForForm(newInitialValues[key]);
            }

            ////////////////////////////////////////////////////////////////////////////////
            // update PV field display values so re-entering edit shows the correct label //
            ////////////////////////////////////////////////////////////////////////////////
            const formField = formFieldsRef.current[key];
            if (formField?.possibleValueProps && freshRecord.displayValues?.get(key))
            {
               formField.possibleValueProps.initialDisplayValue = freshRecord.displayValues.get(key);
            }
         });
         setInitialValues(newInitialValues);
         setFormFields({...formFieldsRef.current});

         ///////////////////////////////////////////////////////////////////////////////////
         // reload widgets, since a save can change what they should show.  the record    //
         // itself is re-fetched above, but widget data is fetched separately and would   //
         // otherwise keep displaying what it loaded before the save.  note the widget    //
         // remount key includes the record id, so this only matters on update - on       //
         // insert the id changing already forces the remount.                            //
         //                                                                               //
         // this bumps the reload counter, which feeds the widget sections' React key -   //
         // so it REMOUNTS them, and they blank out and jump while their data comes back. //
         // that is deliberate for now: some widget types only read their widgetData at   //
         // mount, and the remount is what re-seeds them - RowBuilderWidget seeds its     //
         // model in an effect gated on "not seeded yet", and a parentWidget renders a    //
         // nested DashboardWidgets that is passed no refetch signal at all.  a           //
         // screen-wide IN-PLACE refresh (no unmount, no jump) is the better end state,   //
         // once those widgets re-seed when their data changes.                           //
         ///////////////////////////////////////////////////////////////////////////////////
         saveScheduledWidgetRefetchRef.current = true;
         reloadWidget(null);

         ////////////////////////////
         // show messages directly //
         ////////////////////////////
         setSuccessMessage(`${tableMetaData.label} successfully updated`);

         //////////////////////////////////////////////////////////////////////////////////
         // and REPLACE whatever was in the warning slot, rather than only writing to it //
         // when the save produced a warning.  A child widget can put a message there    //
         // before the save (onChildWarning - "3 records were already in the list and    //
         // were skipped"), and left in place it sits beside the green "successfully     //
         // updated" alert reading as though the save had raised it.                     //
         //////////////////////////////////////////////////////////////////////////////////
         setWarningMessage(updatedRecord.warnings?.length > 0 ? updatedRecord.warnings[0] : null);

         //////////////////////////////////////////////////////////////////////////////////////////////////
         // capture scroll position of the first visible field or section wrapper before switching modes //
         //////////////////////////////////////////////////////////////////////////////////////////////////
         if (options?.scrollCorrectionRef)
         {
            const scrollAnchors = Array.from(document.querySelectorAll("[data-field-name], .form-section-wrapper"));
            for (const el of scrollAnchors)
            {
               const rect = el.getBoundingClientRect();
               if (rect.top >= 0 && rect.top < window.innerHeight)
               {
                  const fieldName = el.getAttribute("data-field-name") ?? el.getAttribute("id");
                  if (fieldName)
                  {
                     options.scrollCorrectionRef.current = {fieldName, yBefore: rect.top};
                  }
                  break;
               }
            }
         }

         switchMode("view", adjusterValues);
         if (initialMode === "view")
         {
            //////////////////////////////////////////////////////////////////////////////////////////////
            // edit was entered via pushState — push a new view entry (keeps /edit in history for Back) //
            //////////////////////////////////////////////////////////////////////////////////////////////
            window.history.pushState(null, "", location.pathname.replace(/\/edit$/, ""));
         }
         else
         {
            ///////////////////////////////////////////////////////
            // direct navigation to /edit URL — replace in place //
            ///////////////////////////////////////////////////////
            window.history.replaceState(null, "", location.pathname.replace(/\/edit$/, ""));
         }
         window.dispatchEvent(new Event("urlchanged"));

         return (freshRecord);
      }
      else
      {
         ////////////
         // create //
         ////////////
         if (!skipNavigation)
         {
            recordAnalytics({category: "tableEvents", action: isCopy ? "saveCopy" : "saveNew", label: tableMetaData?.label});
         }

         bundleAssociationsIntoPayload(valuesToPost);

         const createdRecord = await qController.create(tableName, valuesToPost);

         if (skipNavigation)
         {
            /////////////////////////////////////////////////
            // modal context — don't navigate after create //
            /////////////////////////////////////////////////
            return (createdRecord);
         }

         let warningMsg = null;
         if (createdRecord.warnings?.length > 0)
         {
            warningMsg = createdRecord.warnings[0];
         }

         const path = isCopy
            ? location.pathname.replace(new RegExp(`/${recordId}/copy$`), "/" + createdRecord.values.get(tableMetaData.primaryKeyField))
            : location.pathname.replace(/create$/, createdRecord.values.get(tableMetaData.primaryKeyField));
         navigate(path, {state: {createSuccess: true, warning: warningMsg}});

         return (createdRecord);
      }
   }


   /***************************************************************************
    ** delete the record
    ***************************************************************************/
   async function deleteRecord(): Promise<void>
   {
      recordAnalytics({category: "tableEvents", action: "delete", label: tableMetaData?.label + " / " + record?.recordLabel});

      await qController.delete(tableName, recordId);

      const pathParts = location.pathname.replace(/\/+$/, "").split("/");
      const path = pathParts.slice(0, -1).join("/");
      navigate(path, {state: {deleteSuccess: true}});
   }


   return {
      mode,
      setMode: switchMode,
      record,
      tableMetaData,
      metaData,
      tableSections,
      t1Section,
      nonT1Sections,
      formFields,
      formFieldsBySection,
      initialValues,
      formValidations: effectiveFormValidations,
      fieldRules,
      loading,
      notFoundMessage,
      errorMessage,
      successMessage,
      warningMessage,
      notAllowedError,
      saveRecord,
      deleteRecord,
      handleFieldChange,
      handleFieldBlur,
      reloadWidget,
      reloadRecordData,
      widgetReloadCounters,
      widgetReloadAdditionalParams,
      widgetRefetchSignals,
      allTableProcesses,
      tableVariant,
      collapsibleSectionOpenStates,
      toggleCollapsibleSectionOpenState,
      openCollapsedSectionsWithErrorFields,
      setSuccessMessage,
      setWarningMessage,
      setErrorMessage,
      clearPageLevelAlerts,
      setFieldValueRef,
      formValuesRef,
      disabledFields,
      childListWidgetData,
      showEditChildForm,
      setShowEditChildForm,
      openAddChildRecord,
      openEditChildRecord,
      deleteChildRecord,
      submitEditChildForm,
      bulkAppendChildRecords,
      sectionVisibility,
      addSubValidations,
   };
}
